import {
  queueAccountKey,
  QueueHttpError,
  type QueueAccount,
} from "../shopify-queue/client.ts";
export type PublicationSelection = {
  mode: "all" | "selected";
  product_ids: number[];
  connection_id?: string;
};
export type PublicationReceipt = {
  success: true;
  request_id: string;
  products_queued: number;
  new_jobs: number;
  joined_jobs: number;
  created_at: string;
  connection_id: string | null;
  store: string | null;
};
type Command = { requestId: string; count: number; body: string; store?: string | null };
type State = {
  pending: Command | null;
  busy: boolean;
  error: string;
  success: PublicationReceipt | null;
};
type Storage = Pick<
  globalThis.Storage,
  "getItem" | "setItem" | "removeItem" | "length" | "key"
>;
export async function publicationRequest<T>(
  base: string,
  account: QueueAccount,
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const headers = new Headers(options.headers);
  headers.set("Content-Type", "application/json");
  headers.set("X-Shop-Domain", account.domain);
  if (account.organizationId)
    headers.set("X-Organization-Id", account.organizationId);
  const response = await fetch(`${base}/api/warehouse/sync/stock${path}`, {
    ...options,
    headers,
    credentials: "include",
    cache: "no-store",
  });
  const data = await response.json().catch(() => null);
  if (!response.ok || !data || data.success !== true)
    throw new QueueHttpError(
      data?.error || "The server reply could not be confirmed",
      response.ok ? 500 : response.status,
    );
  return data as T;
}
export function createStockPublication(
  base: string,
  account: QueueAccount,
  storage: Storage,
) {
  const prefix =
    "whenly-stock-publication-v1:" + queueAccountKey(account) + ":";
  let state: State = { pending: null, busy: false, error: "", success: null };
  const restore = () => {
    const keys = Array.from({ length: storage.length }, (_, i) =>
      storage.key(i),
    )
      .filter((v): v is string => !!v && v.startsWith(prefix))
      .sort();
    for (const key of keys) {
      const raw = storage.getItem(key);
      if (!raw) continue;
      const value = JSON.parse(raw) as Command,
        body = JSON.parse(value.body);
      if (
        key !== prefix + value.requestId ||
        body.request_id !== value.requestId ||
        body.expected_user_id !== account.userId ||
        !Number.isSafeInteger(value.count) ||
        value.count < 1
      )
        throw new Error("Saved publication is invalid");
      return value;
    }
    return null;
  };
  try {
    state.pending = restore();
  } catch {
    state.error =
      "Saved publication data could not be read. Browser storage must be available.";
  }
  const listeners = new Set<() => void>();
  const update = (value: Partial<State>) => {
    state = { ...state, ...value };
    listeners.forEach((fn) => fn());
  };
  const retry = async () => {
    if (state.busy || !state.pending) return;
    const pending = state.pending;
    update({ busy: true, error: "", success: null });
    try {
      const result = await publicationRequest<PublicationReceipt>(
        base,
        account,
        "/requests",
        { method: "POST", body: pending.body },
      );
      const selection = JSON.parse(pending.body) as PublicationSelection;
      if (
        result.request_id !== pending.requestId ||
        result.products_queued !== pending.count ||
        !Number.isSafeInteger(result.new_jobs) ||
        result.new_jobs < 0 ||
        !Number.isSafeInteger(result.joined_jobs) ||
        result.joined_jobs < 0 ||
        result.new_jobs + result.joined_jobs !== pending.count ||
        !result.created_at ||
        (selection.connection_id !== undefined && result.connection_id !== selection.connection_id) ||
        (pending.store != null && result.store !== pending.store)
      )
        throw new QueueHttpError(
          "The publication receipt could not be confirmed. Retry the saved request.",
          500,
        );
      storage.removeItem(prefix + pending.requestId);
      update({ pending: restore(), success: result });
    } catch (error) {
      if (
        error instanceof QueueHttpError &&
        [400, 409, 422].includes(error.status)
      ) {
        try {
          storage.removeItem(prefix + pending.requestId);
          update({ pending: restore() });
        } catch {
          /* Keep an uncertain local receipt. */
        }
      }
      update({
        error:
          error instanceof Error
            ? error.message
            : "The outcome is unknown. Retry the saved request.",
      });
    } finally {
      update({ busy: false });
    }
  };
  return {
    snapshot: () => state,
    subscribe: (fn: () => void) => {
      listeners.add(fn);
      return () => {
        listeners.delete(fn);
      };
    },
    retry,
    submit: async (
      selection: PublicationSelection,
      previewHash: string,
      count: number,
      store?: string | null,
    ) => {
      if (state.pending || state.busy) return;
      const requestId = crypto.randomUUID();
      const command = {
        requestId,
        count,
        store,
        body: JSON.stringify({
          ...selection,
          preview_hash: previewHash,
          request_id: requestId,
          expected_user_id: account.userId,
        }),
      };
      try {
        const previous = restore();
        if (previous) {
          update({
            pending: previous,
            error: "Confirm the saved publication first.",
          });
          return;
        }
        storage.setItem(prefix + requestId, JSON.stringify(command));
      } catch {
        update({
          error:
            "The browser could not save this publication. Allow browser storage before trying again.",
        });
        return;
      }
      update({ pending: command, error: "", success: null });
      await retry();
    },
  };
}
